<template>
  <!-- pt-1: 先頭フィールドの浮きラベルが直前の要素と被らないための余白 -->
  <div class="pt-1">
    <v-text-field
      v-if="!fixedDate"
      v-model="date"
      type="date"
      label="営業日"
      dense
      hide-details
      class="mb-4"
    />
    <v-select
      v-model="selectedUserIds"
      :items="selectableCasts"
      item-text="name"
      item-value="id"
      label="キャスト（複数選択可）"
      multiple
      chips
      small-chips
      deletable-chips
      dense
      hide-details
      class="mb-4"
    />
    <div class="d-flex align-center mb-4 pt-1">
      <v-text-field
        v-model="startTime"
        type="time"
        label="出勤（任意）"
        dense
        hide-details
        class="me-2"
      />
      <v-text-field
        v-model="endTime"
        type="time"
        label="退勤（任意）"
        dense
        hide-details
      />
    </div>
    <p
      v-if="error"
      class="error--text text-caption mb-2"
    >
      {{ error }}
    </p>
    <v-btn
      small
      color="primary"
      block
      :ripple="false"
      :disabled="selectedUserIds.length === 0 || !targetDate"
      :loading="submitting"
      @click="submit"
    >
      {{ selectedUserIds.length > 1 ? `${selectedUserIds.length}名のシフトを登録` : 'シフトを登録' }}
    </v-btn>
  </div>
</template>

<script>
import { ref, computed, getCurrentInstance } from '@vue/composition-api'
import WorkScheduleApi from '@/api/waiter/WorkSchedule'
import { buildShiftDateTime } from '@/views/waiter/shiftStaffingUtils'

export default {
  props: {
    casts: {
      type: Array,
      required: true,
    },
    // 指定すると日付固定（日詳細ドロワー用）。null なら日付入力を表示
    fixedDate: {
      type: String,
      default: null,
    },
    // その日すでにシフトがあるキャストの userId（選択肢から除外）
    excludedUserIds: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const date = ref('')
    const selectedUserIds = ref([])
    const startTime = ref('')
    const endTime = ref('')
    const error = ref('')
    const submitting = ref(false)

    const targetDate = computed(() => props.fixedDate || date.value)

    const selectableCasts = computed(() => {
      const excluded = new Set(props.excludedUserIds.map(Number))

      return props.casts.filter(c => !excluded.has(Number(c.id)))
    })

    const reset = () => {
      selectedUserIds.value = []
      startTime.value = ''
      endTime.value = ''
      error.value = ''
    }

    const submit = async () => {
      error.value = ''
      const hasStart = startTime.value !== ''
      const hasEnd = endTime.value !== ''
      if (hasStart !== hasEnd) {
        error.value = '出勤と退勤はセットで入力してください'

        return
      }

      const basePayload = { date: targetDate.value }
      if (hasStart && hasEnd) {
        basePayload.startAt = buildShiftDateTime(targetDate.value, startTime.value)
        basePayload.endAt = buildShiftDateTime(targetDate.value, endTime.value, startTime.value)
      }

      submitting.value = true
      const failed = []
      try {
        // 1人ずつ登録し、重複などで失敗した人はスキップして続行する
        /* eslint-disable no-await-in-loop, no-restricted-syntax */
        for (const userId of selectedUserIds.value) {
          try {
            await WorkScheduleApi.createWorkSchedule({ ...basePayload, userId })
          } catch (e) {
            const cast = props.casts.find(c => Number(c.id) === Number(userId))
            failed.push(cast?.name || `ID:${userId}`)
          }
        }
        /* eslint-enable no-await-in-loop, no-restricted-syntax */

        const okCount = selectedUserIds.value.length - failed.length
        if (okCount > 0) {
          vm.$toast.success(`${okCount}名のシフトを登録しました`)
        }
        if (failed.length > 0) {
          vm.$toast.warning(`登録できませんでした（既に登録済み等）: ${failed.join('・')}`)
        }
        if (okCount > 0) {
          reset()
          emit('created')
        }
      } finally {
        submitting.value = false
      }
    }

    return {
      date,
      selectedUserIds,
      startTime,
      endTime,
      error,
      submitting,
      targetDate,
      selectableCasts,
      submit,
    }
  },
}
</script>
